'use client'

import { useEffect, useState } from 'react'
// @ts-expect-error
import googleTagManager from '@analytics/google-tag-manager'
// @ts-expect-error
import mixpanelPlugin from '@analytics/mixpanel'
import { Analytics } from 'analytics'
import { usePathname, useSearchParams } from 'next/navigation'
import { parse } from 'qs'

import { stripLocaleFromPath } from '~/i18n/routing'
import { useTracking } from '~/providers/tracking-provider'
import { umamiPlugin } from './analytics-umami-plugin'

declare global {
	interface Window {
		mixpanel: any
	}
}

const IS_BROWSER = typeof window !== 'undefined'

const campaignKeywords = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term',
]

export const getSource = () => {
	if (!document.referrer.length) {
		return 'direct'
	}
	if (document.referrer.search('https?://(.*)google.([^/?]*)') === 0) {
		return 'Google'
	}
	if (document.referrer.search('https?://(.*)bing.([^/?]*)') === 0) {
		return 'Bing'
	}
	if (document.referrer.search('https?://(.*)yahoo.([^/?]*)') === 0) {
		return 'Yahoo'
	}
	if (document.referrer.search('https?://(.*)facebook.([^/?]*)') === 0) {
		return 'Facebook'
	}
	if (document.referrer.search('https?://(.*)twitter.([^/?]*)') === 0) {
		return 'Twitter'
	}
	return 'Other'
}

/* Initialize index */
export const analytics = Analytics({
	app: 'cocoon-web',
	// debug: process.env.NEXT_PUBLIC_APP_ENV !== 'prod',
	plugins: IS_BROWSER
		? [
				umamiPlugin({
					src: 'https://analytics.cocoon-space.com/script.js',
					tag: 'web',
					websiteId: process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID as string,
				}),
				googleTagManager({
					containerId: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_CONTAINER_ID,
					enabled: false,
				}),
				mixpanelPlugin({
					customScriptSrc: `${process.env.NEXT_PUBLIC_API_BASE_URL}/mp/lib.min.js`,
					enabled: true,
					options: {
						api_host:
							process.env.NODE_ENV === 'development'
								? 'https://api-js.mixpanel.com'
								: `${process.env.NEXT_PUBLIC_API_BASE_URL}/mp`,
						debug: process.env.NEXT_PUBLIC_APP_ENV !== 'prod',
						loaded() {
							const query = parse(window.location.search)
							const source = getSource()

							const setUser: Record<string, string> = {
								viewportSize: `${window.innerWidth}x${window.innerHeight}`,
							}

							const setUserOnce: Record<string, string> = {
								$created: new Date().toISOString(),
								Source: source,
							}

							// biome-ignore lint/complexity/noForEach: <explanation>
							campaignKeywords.forEach((queryName) => {
								const value = (query[queryName] as string | undefined)?.replace(
									/\+/g,
									' ',
								)

								setUserOnce[`${queryName} [first touch]`] = value || 'none'

								if (value) {
									setUser[`${queryName} [last touch]`] = value
								}
							})

							window.mixpanel?.register({ platform: 'web' })
							window.mixpanel?.register_once({ Source: source })
							window.mixpanel?.people.set(setUser)
							window.mixpanel?.people.set_once(setUserOnce)
						},
					},
					pageEvent: 'Visit',
					token: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN,
				}),
			]
		: [],
})

const searchParamsToObject = (searchParams: URLSearchParams) => {
	const query: Record<string, string | string[]> = {}

	for (const key of new Set(searchParams.keys())) {
		const values = searchParams.getAll(key)

		query[key] = values.length > 1 ? values : values[0]
	}

	return query
}

export const MixPanelTracking = () => {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const { analytics } = useTracking()
	const [previousRoute, setPreviousRoute] = useState('')

	const route = stripLocaleFromPath(pathname ?? '')

	useEffect(() => {
		const query = searchParamsToObject(searchParams)

		analytics.page({
			previousRoute: previousRoute,
			query,
			route,
		})

		setPreviousRoute(route)

		window.mixpanel?.register({
			previousRoute: previousRoute,
			query,
			route,
		})
		// oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
	}, [route, searchParams, analytics.page])

	return null
}

export default MixPanelTracking
